import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { TransactionCategory } from '../../types/financial';
import './Financial.css';

interface CategoryManagerProps {
  categories: TransactionCategory[];
  loading?: boolean;
  onCreate: (category: Omit<TransactionCategory, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onUpdate: (categoryId: string, updates: Partial<TransactionCategory>) => Promise<void>;
  onDelete: (categoryId: string) => Promise<void>;
}

export const CategoryManager: React.FC<CategoryManagerProps> = ({
  categories,
  loading,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TransactionCategory | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<'receita' | 'despesa'>('receita');
  const [color, setColor] = useState('#64748b');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleOpenModal = (category?: TransactionCategory) => {
    if (category) {
      setEditingCategory(category);
      setName(category.name);
      setType(category.type);
      setColor(category.color || '#64748b');
    } else {
      setEditingCategory(null);
      setName('');
      setType('receita');
      setColor('#64748b');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
    setName('');
    setType('receita');
    setColor('#64748b');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Digite um nome para a categoria');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        if (editingCategory.isDefault) {
          alert('Não é possível editar categorias padrão');
          return;
        }
        await onUpdate(editingCategory.id, { name, type, color });
      } else {
        await onCreate({ name, type, color, isDefault: false });
      }
      handleCloseModal();
    } catch (error: any) {
      alert(error.message || 'Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: TransactionCategory) => {
    if (category.isDefault) {
      alert('Não é possível excluir categorias padrão');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir a categoria "${category.name}"?`)) {
      return;
    }

    setDeletingId(category.id);
    try {
      await onDelete(category.id);
    } catch (error: any) {
      alert(error.message || 'Erro ao excluir categoria');
    } finally {
      setDeletingId(null);
    }
  };

  const receitas = categories.filter(c => c.type === 'receita');
  const despesas = categories.filter(c => c.type === 'despesa');

  return (
    <>
      <Card
        title="Categorias"
        actions={
          <Button onClick={() => handleOpenModal()}>
            Nova Categoria
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Carregando categorias...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '24px' }}>
            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
                Receitas
              </h3>
              <div className="ns-category-grid">
                {receitas.map((category) => (
                  <div key={category.id} className="ns-category-item">
                    <div
                      className="ns-category-color"
                      style={{ backgroundColor: category.color || '#64748b' }}
                    />
                    <span className="ns-category-name">{category.name}</span>
                    {!category.isDefault && (
                      <div className="ns-category-actions">
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenModal(category)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(category)}
                          disabled={deletingId === category.id}
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            color: '#ef4444',
                          }}
                        >
                          {deletingId === category.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </div>
                    )}
                    {category.isDefault && (
                      <span className="ns-category-badge">Padrão</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
                Despesas
              </h3>
              <div className="ns-category-grid">
                {despesas.map((category) => (
                  <div key={category.id} className="ns-category-item">
                    <div
                      className="ns-category-color"
                      style={{ backgroundColor: category.color || '#64748b' }}
                    />
                    <span className="ns-category-name">{category.name}</span>
                    {!category.isDefault && (
                      <div className="ns-category-actions">
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenModal(category)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(category)}
                          disabled={deletingId === category.id}
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            color: '#ef4444',
                          }}
                        >
                          {deletingId === category.id ? 'Excluindo...' : 'Excluir'}
                        </Button>
                      </div>
                    )}
                    {category.isDefault && (
                      <span className="ns-category-badge">Padrão</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form className="ns-form" onSubmit={handleSubmit}>
          <label className="ns-field">
            <span>Nome</span>
            <input
              className="ns-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome da categoria"
              required
            />
          </label>

          <label className="ns-field">
            <span>Tipo</span>
            <select
              className="ns-input"
              value={type}
              onChange={(e) => setType(e.target.value as 'receita' | 'despesa')}
              required
            >
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
            </select>
          </label>

          <label className="ns-field">
            <span>Cor</span>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                className="ns-input"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: '60px', height: '40px', padding: '4px' }}
              />
              <input
                className="ns-input"
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#64748b"
                style={{ flex: 1 }}
              />
            </div>
          </label>
        </form>
      </Modal>
    </>
  );
};

